import { AccountInviteStatus, AccountService } from '@qdrant/qdrant-cloud-public-api/account/v1';
import { IAMService } from '@qdrant/qdrant-cloud-public-api/iam/v1';
import { createFileRoute, lazyRouteComponent } from '@tanstack/react-router';
import { assertRpcPermissions } from '@/api/permissions';
import { accountService } from '@/api/services/account';
import { iamService } from '@/api/services/iam';
import { resolveAccountHasRpcPermissions } from '@/router/resolvers/user';

export const Route = createFileRoute('/_authenticated/_dashboard/accounts/$accountId/cloud-access/roles/all-users')({
  beforeLoad: async ({ context: { queryClient }, params: { accountId } }) => {
    // The gate, read from the permissions `$accountId` already awaited: an AccessControlError — rendered
    // as "Access Denied" — when the account may not `read:users`. Checked here rather than by awaiting
    // the users, so the page opens at once and only the table waits on them.
    assertRpcPermissions([IAMService.method.listUsers], { queryClient, accountId });

    // Invites are secondary: the table lists them alongside the users, so an account that may not read
    // them gets the users on their own rather than a refusal on the whole page.
    const canReadInvites = await resolveAccountHasRpcPermissions([AccountService.method.listAccountInvites], {
      queryClient,
      accountId,
    });

    return {
      listUsersQuery: iamService.listUsersQuery({ accountId }),
      listAccountInvitesQuery: canReadInvites
        ? accountService.listAccountInvitesQuery({
            accountId,
            statuses: [AccountInviteStatus.PENDING],
          })
        : undefined,
    };
  },
  // Not awaited: the table reads both with non-suspending queries and shows its own skeleton until the
  // users land; the invites fill in after.
  loader: ({ context: { queryClient, listUsersQuery, listAccountInvitesQuery } }) => {
    void queryClient.prefetchQuery(listUsersQuery);

    if (listAccountInvitesQuery) {
      void queryClient.prefetchQuery(listAccountInvitesQuery);
    }
  },
  component: lazyRouteComponent(() => import('@/components/AccessManagement/AllUsers'), 'AllUsersComponent'),
  pendingMs: 200,
  pendingComponent: lazyRouteComponent(() => import('@/components/AccessManagement/UsersSkeleton'), 'UsersSkeleton'),
});
